import React, { useRef, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { submitChatFeedbackPrompt } from '../../api/stageFunctions';
import type { Turn } from './types';

type Rating = 'bad' | 'fine' | 'good';
interface Props {
  turn: Turn;
  surface: 'homechat' | 'farchat';
  contextId?: string;
  /** Release the app gate only after the server acknowledges the saved rating. */
  onSubmitted?: () => void;
}

/** Required rating form displayed inline below its chat answer. */
const FeedbackPrompt: React.FC<Props> = ({ turn, surface, contextId, onSubmitted }) => {
  const [rating, setRating] = useState<Rating | null>(null);
  const [goodText, setGoodText] = useState('');
  const [badText, setBadText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const submitting = useRef(false);
  const promptId = turn.feedbackRequest?.promptId;
  if (!promptId) return null;

  const handleSubmit = async () => {
    if (!rating || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setError(false);
    try {
      const response = await submitChatFeedbackPrompt({
        promptId, rating, turnId: turn.turn_id, contextId, surface,
        goodComment: goodText.trim(), badComment: badText.trim(),
      });
      if (!response.success) {
        setError(true);
        return;
      }
      onSubmitted?.();
    } catch {
      setError(true);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <Box data-testid="farchat-feedback-prompt" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography fontWeight={600}>How was this response?</Typography>
      <Typography variant="body2">Select a rating and submit your feedback to continue. Comments are optional.</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {(['bad', 'fine', 'good'] as const).map((value) => (
          <Button key={value} variant={rating === value ? 'contained' : 'outlined'}
            disabled={saving} aria-pressed={rating === value}
            data-testid={`farchat-feedback-prompt-${value}`} onClick={() => setRating(value)}>
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </Button>
        ))}
      </Box>
      {rating && <>
        <TextField multiline minRows={2} maxRows={6} fullWidth size="small" label="What was good?"
          disabled={saving} value={goodText} onChange={(event) => setGoodText(event.target.value)}
          inputProps={{ maxLength: 2000, 'data-testid': 'farchat-feedback-prompt-good-comment' }} />
        <TextField multiline minRows={2} maxRows={6} fullWidth size="small" label="What could be better?"
          disabled={saving} value={badText} onChange={(event) => setBadText(event.target.value)}
          inputProps={{ maxLength: 2000, 'data-testid': 'farchat-feedback-prompt-bad-comment' }} />
      </>}
      {error && <Alert severity="error">Unable to save feedback. Your entries are preserved. Please try submitting again.</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={!rating || saving} onClick={handleSubmit}
          data-testid="farchat-feedback-prompt-submit">{saving ? 'Submitting…' : 'Submit'}</Button>
      </Box>
    </Box>
  );
};
export default FeedbackPrompt;
